import { COMM_TEMPLATE_LINK_TAGS } from '@shared/constants/system/comm-template';
import type { BaseTemplateData } from '@shared/types';

/** Escapes a value for insertion into an HTML attribute or text node. */
function escapeHtml(value: string) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const LINK_TAGS: ReadonlySet<string> = new Set<string>(COMM_TEMPLATE_LINK_TAGS);

/**
 * Decodes the character entities editors emit into plain text. Needed on the `stripHtml` path
 * (email subjects, SMS bodies): tag stripping alone leaves entities like `&nbsp;` — which TipTap
 * produces for spaces adjacent to mention chips — as literal text. `&amp;` must decode last so it
 * can't create new entities from already-decoded text.
 */
function decodeHtmlEntities(value: string) {
    return value
        .replace(/&nbsp;|&#160;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;|&apos;/g, "'")
        .replace(/&amp;/g, '&');
}

export function processCommunicationTemplate<D extends BaseTemplateData>(
    template: string,
    tagData: D,
    tags: string[],
    options?: { stripHtml?: boolean; emptyValue?: string },
) {
    const modifiedOptions: NonNullable<Required<typeof options>> = {
        stripHtml: false,
        emptyValue: '-',
        ...options,
    };

    const htmlRegExp = /(<([^>]+)>)/gm;
    const mentionRegExp = /\B@[a-zA-Z0-9_-]+/gi;
    // Used to replace mentions from previous quill-react editor version
    const mentionTagRegExpOld =
        /<span[^>]*class="mention"[^>]*data-value="([^"]+)"[^>]*>.*?<span[^>]*class="ql-mention-denotation-char"[^>]*>@<\/span>[^<]*<\/span>[^<]*<\/span>/gm;
    const mentionTagRegExp =
        /<span[^>]*class="mention"[^>]*data-denotation-char="@"[^>]*data-value="([^"]+)"[^>]*>.*?<span[^>]*>@[^<]*<\/span>[^<]*<\/span>/gm;
    // TipTap mention format - prefer data-label (for custom fields), fallback to data-id (for regular mentions)
    const mentionTagRegExpTipTap =
        /<span[^>]*class="mention"[^>]*(?:data-label="([^"]+)"|data-id="([^"]+)")[^>]*>@?[^<]*<\/span>/gm;

    function mapData(key: string, isHtml?: boolean) {
        const dataKey = tags.find((item) => item === key);
        const hasValue = !!dataKey && (!!tagData[dataKey] || tagData[dataKey] === 0);
        const value = hasValue ? String(tagData[dataKey]) : modifiedOptions.emptyValue;

        if (!isHtml) return value;
        // Merge values are plain text from claimants and handlers, never markup: escaped, they
        // cannot inject into an email body or the public confirmation page. On the `stripHtml`
        // path the escaping survives tag stripping and is undone by `decodeHtmlEntities`.
        const escaped = escapeHtml(value);
        if (modifiedOptions.stripHtml) return escaped;
        // Link tags become a clickable anchor in HTML bodies only (CUR-985). The `stripHtml`
        // paths (subjects, SMS) and the plain-text path above keep the bare URL.
        if (hasValue && dataKey && LINK_TAGS.has(dataKey)) return `<a href="${escaped}">${escaped}</a>`;
        return escaped.replace(/\n/g, '<br>');
    }

    if (htmlRegExp.test(template)) {
        const processedContent = template
            .replace(mentionTagRegExpOld, (_, value: string) => mapData(value, true))
            .replace(mentionTagRegExp, (_, value: string) => mapData(value, true))
            // TipTap format: prefer data-label (custom fields), fallback to data-id (regular mentions)
            .replace(mentionTagRegExpTipTap, (_, label: string, id: string) => mapData(label || id, true));

        return modifiedOptions.stripHtml
            ? decodeHtmlEntities(processedContent.replace(htmlRegExp, ''))
            : processedContent;
    }

    return template.replace(mentionRegExp, (match) => mapData(match.substring(1), false));
}

export function appendSignatureToEmailCommunicationTemplate({
    template,
    signature,
}: {
    template: string;
    signature: string | undefined;
}) {
    return signature ? `${template}<br/>${signature}` : template;
}
